/**
 * Expenses repository — the ONLY place touching the `expenses` and
 * `owner_payouts` tables. FINANCIAL slice (money-exact).
 *
 * Strangler-Fig per-slice migration (manifest-6, Track A slice A5):
 *   Step 1 (done) — consolidated scattered `supabase.from("expenses" | "owner_payouts")`
 *                   calls here. Query shapes are BYTE-IDENTICAL to what existed inline.
 *   Step 2 (done) — guarded NestJS endpoints (`apps/api/src/expenses/*`), gated under
 *                   the `finance.*` permission key.
 *   Step 3 (done) — the STAFF Finance-dashboard ops flipped to the Nest client.
 *   Step 4 (HERE) — every `expenses`-table Supabase fallback is REMOVED. The
 *                   `"Active-role view expenses"` policy was dropped (Track E,
 *                   asserted ABSENT by
 *                   supabase/db-tests/tests/005_two_layer_access_model.sh) and the
 *                   only surviving PERMISSIVE SELECT on `public.expenses` is the
 *                   super-admin one — so those fallbacks returned a silent `[]`
 *                   (or 42501 on a RETURNING write) for every other session,
 *                   masking a Nest outage as an empty Finance dashboard.
 *                   `create` and `updateImages` had already been made Nest-only
 *                   for exactly this reason; this completes the set.
 *
 * ── Flip scope (owner-vs-staff authz split) ──────────────────────────────────
 *   NEST-ONLY, no fallback: `list`, `getById`, `create`, `update`, `updateImages`,
 *     `softDelete`, `listByProperty`, the 9 aggregate/report projections
 *     (`listStats`, `listSummary`, `listMonthly`, `listFinancial`, `listTotals`,
 *     `listRecent`, `listForIncomeStatement`, `listForPropertyPerformance`,
 *     `listForOperatingExpensesReport`) and the owner-context reads
 *     (`listOwnerExpensesForFinancials`, `listOwnerExpensesForDocuments`,
 *     `listForOwnerView`, `listForOwnerPayouts`). All read `public.expenses`.
 *     `nestRequest` forwards `x-active-role` so the server oracle scopes
 *     employee/owner reads; employee property-scoping is resolved server-side.
 *   FLIPPED to Nest, Supabase fallback still ARMED:
 *     - `generateInvoiceFromExpense` — its fallback is the
 *       `generate_invoice_from_expense` RPC, which is SECURITY DEFINER and so
 *       is NOT gated by the retired SELECT policy; it still works. It keeps the
 *       duplicate-write guard (falls back only on a known-uncommitted 4xx;
 *       ambiguous Nest failures rethrow — the RPC has no double-generation check).
 *   KEPT on Supabase:
 *     - The `owner_payouts`-table methods (`listOwnerPayouts`,
 *       `listOwnerPayoutAmounts`, `createOwnerPayout`) — a separate table, out of
 *       the `expenses` SELECT-RLS retirement scope.
 *
 * FINANCIAL invariants preserved across the flip:
 *   - `generate_invoice_from_expense` is an ATOMIC Postgres RPC (invoice insert +
 *     expense reconciliation). The Nest path FORWARDS to the SAME RPC server-side
 *     (no TS re-home, no double-apply); the armed fallback calls it directly.
 *   - VAT is pre-computed by the caller (`useCreateExpense`/`useUpdateExpense`);
 *     both paths persist the same money fields → halala-identical totals.
 *   - SOFT delete only (`deleted_at`), matching the existing read filters.
 *
 * The Nest-only methods require a configured API URL — `nestRequest` throws a
 * typed NestRequestError when `VITE_API_URL` is unset, instead of silently
 * serving an empty list from a dead RLS path. List/detail responses are parsed
 * through the shared `expenseSchema` (a too-strict DTO used to cause a silent
 * fallback — the #1699 lesson; it now surfaces as a parse error).
 *
 * Deferred to a follow-up (cross-domain reads — the matrix "read-by" column):
 *   - src/hooks/usePropertyAggregates.ts line 178 (cross-domain property aggregates)
 *   - src/hooks/useReportsData.ts line 79 (same useReportsData hook migrated partially)
 *
 * See: docs/migration/domain-debugging-matrix.md (finance row)
 *      docs/handoff/open/migration-a5-expenses.md
 */
import {
  isDefinitiveNotFound,
  reportNestFallbackOrRethrowUnknownCommit,
} from "@/lib/nestFallback";
import { z } from "zod";
import { expenseSchema } from "@dallali/shared";
import { supabase } from "@/integrations/supabase/client";
import { serializeError } from "@/lib/sentry";
import type {
  ExpenseWithRelations,
  ExpenseDetail,
  ExpenseType,
  PaymentResponsibility,
} from "@/types/database";
import type { Database } from "@/integrations/supabase/types";
import { API_ROUTES } from "@/api/routes";
import { API_URL } from "@/services/api";
import { makeNestRequest } from "@/services/nestRequest";

type ExpenseInsert = Database["public"]["Tables"]["expenses"]["Insert"];


// Authenticated Nest request bound to the "expenses" slice — shared transport
// (timeout + retry + typed NestRequestError). See src/services/nestRequest.ts.
// UNSCOPED: no x-active-role forwarded/required. `expenses` has NO
// RowScopeGuard anywhere (unlike units/properties/contracts/invoices) — every
// endpoint either ignores the role entirely (list/getById/create/update/
// remove/generateInvoiceFromExpense, ownerFinancials/ownerDocuments — pure
// owner-context via currentOwnerId, no activeRole) or CONSULTS it via the
// service-layer oracle (ExpensesAggregateService) WITHOUT 403ing on absence —
// see nestRequestRoleAware below. Must never be blocked/rerouted to the
// Supabase fallback by a missing/unresolved active role.
const nestRequest = makeNestRequest("expenses");

// Role-AWARE (not role-REQUIRED) variant: the 9 aggregate/report endpoints +
// the two staff owner-context reads (byProperty, ownerView, ownerPayouts) run
// through ExpensesAggregateService.resolveScope, which reads x-active-role to
// pick the employee/owner/admin branch — but degrades GRACEFULLY to `[]` when
// the role is absent (or the caller lacks finance.view) rather than 403ing;
// staff holding `finance.create` get the full-company scope regardless of the
// header (a fast path that doesn't even consult the role). Forward the header
// best-effort when present; NEVER short-circuit before the fetch — unlike a
// true RowScopeGuard endpoint, blocking here would deny a request Nest would
// have happily answered (issue #3278 rework).
const nestRequestRoleAware = makeNestRequest("expenses", { forwardActiveRole: true });

// ─── Shared option types ──────────────────────────────────────────

export interface ExpenseScopeOptions {
  companyId: string;
}

export interface ExpenseDateRangeFilter {
  startDate?: string;
  endDate?: string;
}

export interface ExpenseAssignedPropertyFilter {
  assignedPropertyIds: string[] | null;
}

// ─── Light projection types ───────────────────────────────────────

export interface ExpenseStatsRow {
  total_amount: number | null;
  is_invoiced: boolean | null;
  generated_invoice_id: string | null;
}

export interface ExpenseSummaryRow {
  id: string;
  created_at: string;
  total_amount: number | null;
  is_invoiced: boolean | null;
  generated_invoice_id: string | null;
}

export interface ExpenseMonthlyRow {
  total_amount: number | null;
  expense_date: string | null;
}

export interface ExpenseFinancialRow {
  total_amount: number | null;
  expense_date: string | null;
}

export interface ExpenseIncomeStatementRow {
  total_amount: number | null;
  expense_type: string | null;
}

export interface ExpensePropertyPerformanceRow {
  property_id: string | null;
  total_amount: number | null;
}

export interface ExpenseTotalsRow {
  total_amount: number | null;
  created_at: string;
}

export interface ExpenseOwnerPayoutsRow {
  property_id: string | null;
  total_amount: number | null;
}

/** Owner-portal financial-summary projection (useOwnerFinancials). */
export interface OwnerFinancialsExpenseRow {
  total_amount: number | null;
  expense_date: string | null;
  property_id: string | null;
}

/** Staff owner-statement projection (useOwnerFinancialSummary, useOwners.ts). */
export interface OwnerViewExpenseRow {
  id: string;
  total_amount: number | null;
  expense_date: string | null;
  description: string | null;
  expense_type: string | null;
  property_id: string | null;
  property: { name: string | null; name_en: string | null } | null;
  unit: { unit_number: string | null } | null;
  payment_responsibility: string | null;
}

export interface OwnerPayoutRow {
  owner_id: string;
  amount: number | null;
}

export interface CreateOwnerPayoutData {
  ownerId: string;
  amount: number;
  revenueAmount: number;
  expensesAmount: number;
  managementFee: number;
  notes?: string;
  paymentMethod?: string;
  referenceNumber?: string;
}

export interface RecentExpenseRow {
  id: string;
  description: string | null;
  total_amount: number | null;
  expense_date: string | null;
  vendor_name: string | null;
  expense_type: string | null;
  property: { name: string } | null;
  unit: { unit_number: string | null } | null;
}

export interface CreateExpenseData {
  property_id?: string;
  unit_id?: string;
  contract_id?: string;
  expense_date: string;
  expense_type: ExpenseType;
  payment_responsibility: PaymentResponsibility;
  amount: number;
  vat_rate?: number;
  vendor_name?: string;
  description: string;
  invoice_url?: string;
  images?: string[];
}

export interface UpdateExpenseData {
  id: string;
  expense_date?: string;
  expense_type?: ExpenseType;
  payment_responsibility?: PaymentResponsibility;
  amount?: number;
  vat_rate?: number;
  vendor_name?: string;
  description?: string;
  invoice_url?: string;
  images?: string[];
}

export interface GenerateInvoiceFromExpenseData {
  expenseId: string;
  companyId: string;
  payment_responsibility: PaymentResponsibility;
  contract_id?: string | null;
  property_id?: string | null;
  unit_id?: string | null;
  total_amount: number;
  description: string;
  expense_date: string;
}

// ─── Repository ───────────────────────────────────────────────────

export const expensesRepository = {
  /**
   * Canonical expenses list with cross-schema relations, newest first.
   * Company-scoped + soft-deleted excluded. Employee scope applied when
   * assignedPropertyIds is a non-empty array.
   */
  async list(
    _scope: ExpenseScopeOptions & ExpenseAssignedPropertyFilter,
  ): Promise<ExpenseWithRelations[]> {
    const rows = await nestRequest<unknown[]>(API_ROUTES.expenses.list());
    return z.array(expenseSchema).parse(rows) as unknown as ExpenseWithRelations[];
  },

  /**
   * Fetch a single expense with full relations for the detail view.
   *
   * Staff op — Nest-only (`GET /expenses/:id`). The detail response is
   * validated through the shared `expenseSchema`. A definitive app-level 404
   * (missing / cross-tenant row) resolves to null, matching the old
   * `.maybeSingle()` null.
   */
  async getById(id: string, _companyId: string): Promise<ExpenseDetail | null> {
    try {
      const row = await nestRequest<unknown>(API_ROUTES.expenses.getById(id));
      return (row ? expenseSchema.parse(row) : null) as unknown as ExpenseDetail | null;
    } catch (err) {
      if (isDefinitiveNotFound(err)) return null; // app-level 404 = the real answer (#3397 class)
      throw err;
    }
  },

  /**
   * Expenses for a single property's detail tab (used by usePropertyExpenses).
   * Full row + `unit`/`contract` relations, company- + property-scoped, newest
   * expense_date first, soft-deleted excluded.
   *
   * Nest-only (`GET /expenses/by-property/:propertyId`, oracle-scoped
   * server-side). The rows are validated through the shared `expenseSchema`
   * (relations are optional/passthrough, so the unit/contract-only projection
   * parses cleanly). Mirrors `list`/`getById`.
   */
  async listByProperty(companyId: string, propertyId: string): Promise<ExpenseWithRelations[]> {
    const rows = await nestRequestRoleAware<unknown[]>(API_ROUTES.expenses.byProperty(propertyId));
    return z.array(expenseSchema).parse(rows) as unknown as ExpenseWithRelations[];
  },

  /**
   * Owner-portal financial-summary expenses (used by useOwnerFinancials).
   * Light projection — total_amount / expense_date / property_id — for the
   * owner's contract-derived properties. Optional `range` bounds
   * `expense_date` server-side (month/year time filter, CEO ask) — same
   * `expenseDate` column the company-wide `listFinancial`/`listSummary`
   * date-filtered variants use.
   *
   * Nest-only (`GET /expenses/owner-financials`, owner authz context — the
   * server resolves the caller's owners.id and contract-property set).
   * Partial projection → NOT parsed through expenseSchema (it requires the
   * full row, so parsing would reject valid partial rows).
   */
  async listOwnerExpensesForFinancials(
    companyId: string,
    propertyIds: string[],
    range?: ExpenseDateRangeFilter,
  ): Promise<OwnerFinancialsExpenseRow[]> {
    if (propertyIds.length === 0) return [];
    const rows = await nestRequest<unknown[]>(
      API_ROUTES.expenses.ownerFinancials(range?.startDate, range?.endDate),
    );
    return rows as OwnerFinancialsExpenseRow[];
  },

  /**
   * Owner-portal documents-tab expenses (used by useOwnerDocuments).
   * Full row + property/unit relations, newest created_at first, for the owner's
   * contract-derived properties.
   *
   * Nest-only (`GET /expenses/owner-documents`, owner authz context).
   * Full-row projection → parsed through the shared `expenseSchema`
   * (relations passthrough), mirroring `listByProperty`.
   */
  async listOwnerExpensesForDocuments(
    companyId: string,
    propertyIds: string[],
  ): Promise<ExpenseWithRelations[]> {
    if (propertyIds.length === 0) return [];
    const rows = await nestRequest<unknown[]>(API_ROUTES.expenses.ownerDocuments());
    return z.array(expenseSchema).parse(rows) as unknown as ExpenseWithRelations[];
  },

  /**
   * Staff owner-statement expenses for a single owner (used by
   * useOwnerFinancialSummary, useOwners.ts). Returns ALL expenses on the owner's
   * properties (both payment responsibilities — the caller filters
   * `payment_responsibility = 'owner'`), newest expense_date first.
   *
   * Nest-only (`GET /expenses/owner-view/:ownerId`, staff finance oracle ∩
   * the target owner's properties). Partial projection → cast, not
   * expenseSchema-parsed.
   */
  async listForOwnerView(
    companyId: string,
    ownerId: string,
    propertyIds: string[],
  ): Promise<OwnerViewExpenseRow[]> {
    if (propertyIds.length === 0) return [];
    const rows = await nestRequestRoleAware<unknown[]>(API_ROUTES.expenses.ownerView(ownerId));
    return rows as OwnerViewExpenseRow[];
  },

  /**
   * Insert a new expense. VAT computation is the caller's responsibility
   * (see useCreateExpense for the vat_amount / total_amount derivation) — both
   * paths persist the same money fields, so totals are halala-identical.
   *
   * Staff op — Nest-only (`POST /expenses`, company injected from the JWT). The
   * Nest response is the inserted row, validated through the shared
   * `expenseSchema`.
   *
   * The Supabase fallback was retired: it used `.insert().select()` (INSERT ...
   * RETURNING), and 20260605120000 dropped the non-super-admin SELECT policy on
   * expenses — the RETURNING 42501s, so the fallback could only ever turn a
   * Nest blip into a hard RLS error (same failure mode as the service_requests
   * owner create, fixed the same way). `nestRequest` throws a clear
   * NestRequestError when VITE_API_URL is unset.
   */
  async create(insertData: ExpenseInsert) {
    const row = await nestRequest<unknown>(API_ROUTES.expenses.create(), {
      method: "POST",
      // company_id is injected server-side from the JWT; never sent from the body.
      body: JSON.stringify(insertData),
    });
    return expenseSchema.parse(row);
  },

  /**
   * Update expense fields. Company-scoped for multi-tenant safety.
   *
   * Staff op — Nest-only (`PUT /expenses/:id`). Returns the updated row
   * (parsed but discarded — the caller ignores the return).
   */
  async update(
    id: string,
    companyId: string,
    updates: Partial<{
      expense_date: string;
      expense_type: ExpenseType;
      payment_responsibility: PaymentResponsibility;
      amount: number;
      vat_rate: number;
      vat_amount: number;
      total_amount: number;
      vendor_name: string;
      description: string;
      invoice_url: string;
      images: string[];
    }>,
  ) {
    await nestRequest<unknown>(API_ROUTES.expenses.update(id), {
      method: "PUT",
      body: JSON.stringify(updates),
    });
    return;
  },

  /**
   * Update expense images only — Nest-only via the guarded `PUT /expenses/:id`
   * (UpdateExpenseDto whitelists `images`; company scope resolved from the JWT).
   * Returns the updated row, validated through the shared `expenseSchema`.
   *
   * Previously a direct-Supabase `.update().select()` (UPDATE ... RETURNING):
   * post-20260605120000 (non-super-admin SELECT policy on expenses dropped) the
   * RETURNING 42501'd, so this method was hard-broken for every role except
   * super_admin. A missing/foreign row now surfaces as the endpoint's 404
   * (NestRequestError) instead of the old "Expense not found or access denied".
   */
  async updateImages(expenseId: string, images: string[]) {
    const row = await nestRequest<unknown>(API_ROUTES.expenses.update(expenseId), {
      method: "PUT",
      body: JSON.stringify({ images }),
    });
    return expenseSchema.parse(row);
  },

  /**
   * Soft-delete an expense. Company-scoped. The `expenses` table has a
   * `deleted_at` column and ALL read methods filter `.is("deleted_at", null)`,
   * so deletion must set `deleted_at` (a hard `.delete()` destroys
   * FK-referenced history and violates the soft-delete convention).
   *
   * Staff op — Nest-only (`DELETE /expenses/:id`, 204). The guarded endpoint
   * performs the soft-delete server-side (`softDeleteOrThrow`).
   */
  async softDelete(id: string, companyId: string) {
    await nestRequest<void>(API_ROUTES.expenses.remove(id), { method: "DELETE" });
    return;
  },

  // ─── Aggregate / stats projections ───────────────────────────

  /**
   * Stats projection — total_amount / is_invoiced / generated_invoice_id.
   * Used by useFinancialStats.
   *
   * Nest-only (`GET /expenses/stats`) since the Step-4 flip.
   */
  async listStats(companyId: string): Promise<ExpenseStatsRow[]> {
    const rows = await nestRequestRoleAware<unknown[]>(API_ROUTES.expenses.stats());
    return rows as ExpenseStatsRow[];
  },

  /**
   * Summary projection — id / created_at / total_amount / is_invoiced /
   * generated_invoice_id. Used by useFinanceSummaryStats (date-filtered).
   *
   * Nest-only (`GET /expenses/summary`) since the Step-4 flip.
   */
  async listSummary(companyId: string, filter?: ExpenseDateRangeFilter): Promise<ExpenseSummaryRow[]> {
    const params = new URLSearchParams();
    if (filter?.startDate) params.set("startDate", filter.startDate);
    if (filter?.endDate) params.set("endDate", filter.endDate);
    const qs = params.toString();
    const rows = await nestRequestRoleAware<unknown[]>(`${API_ROUTES.expenses.summary()}${qs ? "?" + qs : ""}`);
    return rows as ExpenseSummaryRow[];
  },

  /**
   * Monthly chart projection (expense_date filtered, gte).
   * Used by useMonthlyRevenueExpenses.
   *
   * Nest-only (`GET /expenses/monthly`) since the Step-4 flip.
   */
  async listMonthly(companyId: string, sinceDate: string): Promise<ExpenseMonthlyRow[]> {
    const params = new URLSearchParams({ since: sinceDate });
    const rows = await nestRequestRoleAware<unknown[]>(`${API_ROUTES.expenses.monthly()}?${params.toString()}`);
    return rows as ExpenseMonthlyRow[];
  },

  /**
   * Filtered financial stats projection (expense_date-filtered).
   * Used by useFilteredFinancialStats and useReportsData.
   *
   * Nest-only (`GET /expenses/financial`) since the Step-4 flip.
   */
  async listFinancial(companyId: string, filter?: ExpenseDateRangeFilter): Promise<ExpenseFinancialRow[]> {
    const params = new URLSearchParams();
    if (filter?.startDate) params.set("startDate", filter.startDate);
    if (filter?.endDate) params.set("endDate", filter.endDate);
    const qs = params.toString();
    const rows = await nestRequestRoleAware<unknown[]>(`${API_ROUTES.expenses.financial()}${qs ? "?" + qs : ""}`);
    return rows as ExpenseFinancialRow[];
  },

  /**
   * Income-statement projection — total_amount / expense_type.
   * Used by IncomeStatementReport and CashFlowReport.
   *
   * Nest-only (`GET /expenses/income-statement`) since the Step-4 flip.
   */
  async listForIncomeStatement(companyId: string): Promise<ExpenseIncomeStatementRow[]> {
    const rows = await nestRequestRoleAware<unknown[]>(API_ROUTES.expenses.incomeStatement());
    return rows as ExpenseIncomeStatementRow[];
  },

  /**
   * Property performance projection — property_id / total_amount.
   * Used by PropertyPerformanceReport.
   *
   * Nest-only (`GET /expenses/property-performance`) since the Step-4 flip.
   */
  async listForPropertyPerformance(companyId: string): Promise<ExpensePropertyPerformanceRow[]> {
    const rows = await nestRequestRoleAware<unknown[]>(API_ROUTES.expenses.propertyPerformance());
    return rows as ExpensePropertyPerformanceRow[];
  },

  /**
   * Totals projection (created_at-filtered). Used by TotalsSummaryCards
   * and InvoicesVsExpensesChart.
   *
   * Nest-only (`GET /expenses/totals`) since the Step-4 flip.
   */
  async listTotals(companyId: string, filter?: ExpenseDateRangeFilter): Promise<ExpenseTotalsRow[]> {
    const params = new URLSearchParams();
    if (filter?.startDate) params.set("startDate", filter.startDate);
    if (filter?.endDate) params.set("endDate", filter.endDate);
    const qs = params.toString();
    const rows = await nestRequestRoleAware<unknown[]>(`${API_ROUTES.expenses.totals()}${qs ? "?" + qs : ""}`);
    return rows as ExpenseTotalsRow[];
  },

  /**
   * Owner-scoped expenses for owner payout calculation.
   * Filtered by payment_responsibility='owner' and a set of property IDs.
   *
   * Nest-only (`GET /expenses/owner-payouts`, staff finance oracle ∩ the
   * company's owner-linked properties — the server re-derives the same
   * `properties WHERE owner_id IS NOT NULL` set the caller passes here).
   * Light projection → cast, not expenseSchema-parsed.
   */
  async listForOwnerPayouts(
    companyId: string,
    propertyIds: string[],
  ): Promise<ExpenseOwnerPayoutsRow[]> {
    if (propertyIds.length === 0) return [];

    const rows = await nestRequestRoleAware<unknown[]>(API_ROUTES.expenses.ownerPayouts());
    return rows as ExpenseOwnerPayoutsRow[];
  },

  /**
   * Operating expenses report projection — full details with property relation.
   *
   * Nest-only (`GET /expenses/operating-expenses-report`) since the Step-4
   * flip.
   */
  async listForOperatingExpensesReport(companyId: string): Promise<Array<{
    id: string;
    total_amount: number | null;
    expense_type: string | null;
    expense_date: string | null;
    vendor_name: string | null;
    description: string | null;
    property: { name: string } | null;
  }>> {
    const rows = await nestRequestRoleAware<unknown[]>(API_ROUTES.expenses.operatingExpensesReport());
    return rows as Array<{
      id: string;
      total_amount: number | null;
      expense_type: string | null;
      expense_date: string | null;
      vendor_name: string | null;
      description: string | null;
      property: { name: string } | null;
    }>;
  },

  /**
   * Recent expenses for the dashboard widget (newest first, limited).
   *
   * Nest-only (`GET /expenses/recent`) since the Step-4 flip.
   */
  async listRecent(companyId: string, limit = 2): Promise<RecentExpenseRow[]> {
    const params = new URLSearchParams({ limit: String(limit) });
    const rows = await nestRequestRoleAware<unknown[]>(`${API_ROUTES.expenses.recent()}?${params.toString()}`);
    return rows as RecentExpenseRow[];
  },

  // ─── owner_payouts ────────────────────────────────────────────

  /**
   * List owner payouts — owner_id / amount. Company-scoped.
   * Used by useOwnerPayouts to compute net amounts per owner.
   */
  async listOwnerPayouts(companyId: string): Promise<OwnerPayoutRow[]> {
    const { data, error } = await supabase
      .from("owner_payouts")
      .select("owner_id, amount")
      .eq("company_id", companyId);

    if (error) {
      console.error("Error fetching owner payouts:", serializeError(error));
      throw error;
    }

    return (data ?? []) as OwnerPayoutRow[];
  },

  /**
   * List owner payouts with amount only. Used by report pages (BalanceSheet,
   * CashFlow).
   */
  async listOwnerPayoutAmounts(companyId: string): Promise<{ amount: number | null }[]> {
    const { data, error } = await supabase
      .from("owner_payouts")
      .select("amount")
      .eq("company_id", companyId);

    if (error) {
      console.error("Error fetching owner payout amounts:", serializeError(error));
      throw error;
    }

    return (data ?? []) as { amount: number | null }[];
  },

  /**
   * Insert an owner payout record. Company-scoped.
   */
  async createOwnerPayout(companyId: string, data: CreateOwnerPayoutData) {
    const { error } = await supabase
      .from("owner_payouts")
      .insert({
        company_id: companyId,
        owner_id: data.ownerId,
        amount: data.amount,
        revenue_amount: data.revenueAmount,
        expenses_amount: data.expensesAmount,
        management_fee: data.managementFee,
        notes: data.notes,
        payment_method: data.paymentMethod || "bank_transfer",
        reference_number: data.referenceNumber,
        period_start: new Date(
          new Date().getFullYear(),
          new Date().getMonth(),
          1,
        ).toISOString().split("T")[0],
        period_end: new Date().toISOString().split("T")[0],
      });

    if (error) {
      console.error("Error creating owner payout:", serializeError(error));
      throw error;
    }
  },

  // ─── Atomic RPC (kept in Postgres, invoked via Nest or directly) ─────────────

  /**
   * Convert an expense to an invoice via the ATOMIC `generate_invoice_from_expense`
   * Postgres RPC (invoice insert + expense `is_invoiced`/`generated_invoice_id`
   * reconciliation in one transaction). The RPC stays in PG — it is NEVER
   * re-homed to TypeScript and NEVER double-applied.
   *
   * Staff op — flipped to Nest (`POST /expenses/:id/generate-invoice`, which
   * FORWARDS to the SAME RPC server-side using company_id from the JWT). The
   * Supabase fallback calls the RPC directly. The RPC result shape varies, so it
   * is passed through unparsed.
   */
  async generateInvoiceFromExpense({
    expenseId,
    companyId,
    payment_responsibility,
    contract_id,
    property_id,
    unit_id,
    total_amount,
    description,
    expense_date,
  }: GenerateInvoiceFromExpenseData) {
    if (API_URL) {
      try {
        return await nestRequest<unknown>(API_ROUTES.expenses.generateInvoice(expenseId), {
          method: "POST",
          body: JSON.stringify({
            payment_responsibility,
            contract_id: contract_id ?? null,
            property_id: property_id ?? null,
            unit_id: unit_id ?? null,
            total_amount,
            description,
            expense_date,
          }),
        });
      } catch (err) {
        // Write guard: `generate_invoice_from_expense` has NO double-generation
        // check — a retry after a committed Nest call INSERTs a second invoice
        // and repoints `generated_invoice_id` (a real duplicate-money record).
        // Only a known-uncommitted 4xx may retry through Supabase.
        reportNestFallbackOrRethrowUnknownCommit("expenses.generateInvoiceFromExpense", err);
      }
    }
    const { data, error } = await supabase.rpc("generate_invoice_from_expense", {
      p_company_id: companyId,
      p_expense_id: expenseId,
      p_payment_responsibility: payment_responsibility,
      p_contract_id: contract_id || undefined,
      p_property_id: property_id || undefined,
      p_unit_id: unit_id || undefined,
      p_total_amount: total_amount,
      p_description: description,
      p_expense_date: expense_date,
    });

    if (error) {
      console.error("Error generating invoice from expense:", serializeError(error));
      throw error;
    }

    return data;
  },
};
